import Avo from 'analytics/Avo';
import type { PricingList } from 'api/types/pricing.types';
import { CareemBackButton } from 'assets/icons/careem';
import cx from 'classnames';
import HeaderBackButton from 'components/molecules/HeaderBackButton';
import { ServiceLoaderHorizontal } from 'components/molecules/Loaders';
import { BUTTON_NAMES } from 'constant/button-names';
import { useConfig } from 'context/app-config';
import { useBagServices } from 'hooks/useBagServices';
import React, { type FC, memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePricingUIStore } from 'store/pricing.ui.store';
import { useTheme } from 'styled-components';

import { BagIconMapper, StyledBackButton, StyledHeader, StyledRoundButton } from '../pricing.styles';
import styles from '../styles.module.scss';

interface ServicesHeaderProps {
  isBottomSheet?: boolean;
  onClose?: () => void;
}

const ServicesStickyHeader: FC<ServicesHeaderProps> = ({ onClose, isBottomSheet }) => {
  const navigate = useNavigate();
  const { services, isLoading } = useBagServices();
  const pricingType = usePricingUIStore.use.pricingType();
  const config = useConfig();
  const setPricingType = usePricingUIStore.use.setPricingType();
  const theme = useTheme();

  const getBagIcon = React.useCallback((getType: PricingList.ServiceTypes) => BagIconMapper.get(getType)?.icon, []);

  const handleClick = React.useCallback(
    (selectedType: PricingList.ServiceTypes) => {
      if (selectedType) {
        setPricingType(selectedType);
      } else {
        navigate(`/pricing/${selectedType}`, {
          replace: true,
          state: { disableAnimation: true },
        });
      }
    },
    [navigate, isBottomSheet]
  );

  const onServiceClicked = useCallback(
    (service: PricingList.ServiceTypes) => () => {
      Avo.estimatorBagSelected({ bagType: service });
      handleClick(service);
    },
    [handleClick]
  );

  const onGoBack = useCallback(() => {
    navigate(-1);
  }, []);

  const Services = useMemo(
    () =>
      isLoading ? (
        <ServiceLoaderHorizontal />
      ) : (
        <>
          {(isBottomSheet || config?.showPricingRoundedBackButton) && (
            <StyledBackButton buttonName={BUTTON_NAMES.GO_BACK} onClick={onClose ?? onGoBack}>
              <CareemBackButton />
            </StyledBackButton>
          )}
          {services?.map(([service]) => {
            if (service === 'THE_FINERY') return null;

            return (
              <StyledRoundButton buttonName={`pricing ${service}`} isActive={pricingType === service} onClick={onServiceClicked(service)}>
                {getBagIcon(service)}
              </StyledRoundButton>
            );
          })}
        </>
      ),
    [isLoading, isBottomSheet, onClose, services, pricingType, onServiceClicked, getBagIcon, config?.showPricingRoundedBackButton]
  );

  const isNotSticky = useMemo(
    () => Boolean(isBottomSheet) || Boolean(theme?.components?.pages?.pricing?.noStickyHeader),
    [isBottomSheet, config?.showPricingRoundedBackButton]
  );

  return (
    <StyledHeader $isnotsticky={`${Boolean(isNotSticky)}`} className={cx('bg-white py-3', styles.header)}>
      {!isBottomSheet && config?.showBackButton && !config?.showPricingRoundedBackButton && (
        <div className="ml-1">
          <HeaderBackButton />
        </div>
      )}
      {Services}
    </StyledHeader>
  );
};

export default memo(ServicesStickyHeader);
